<template>
  <div>
    <div class="form-group" data-test="edit-bank-account-form">
      <label class="col-md-4 control-label">{{ $t('Country of bank') }}</label>
      <div class="col-md-4">
        <select
          id="bank-country-select"
          v-model="country"
          v-tooltip:top="$t('Select your country here')"
          name="bank[bankCountry]"
          class="form-control"
        >
          <option
            v-for="option in countryOptions"
            ref="countrySelect"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
        <a v-if="!showAllCountries" class="quiet pointer" @click="showInternationalOptions">
          {{ $t('Add international options') }}
        </a>
        <a v-if="showAllCountries" class="quiet pointer" @click="hideInternationalOptions">
          {{ $t('Remove international options') }}
        </a>
      </div>
    </div>

    <WdsAlert
      v-if="!countryIsSupported && !hasManualPaymentDetails"
      warning
      class="col-lg-offset-4 col-lg-6 tw-mb-4"
    >
      <BaseHtml :html="translationNeedSupportBankDetailsHtml" />
    </WdsAlert>

    <template v-if="!countryIsSupported && hasManualPaymentDetails">
      <div class="form-group">
        <label class="col-md-4 control-label">{{ $t('Account holder’s name') }}</label>
        <div class="col-md-6">
          <div>
            <input v-model="bankAccountInternal.beneficiary_name" class="form-control" disabled />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="col-md-4 control-label">{{ $t('Name of bank') }}</label>
        <div class="col-md-6">
          <input v-model="bankAccountInternal.bank_name" class="form-control" disabled />
        </div>
      </div>
      <div class="form-group">
        <label class="col-md-4 control-label">{{ $t('Bank details') }}</label>
        <div class="col-md-6">
          <WdsInputArea
            :model-value="bankAccount?.bban?.manual_payment_details"
            class="form-control tw-mb-4"
            disabled
          />
          <WdsAlert>
            <BaseHtml :html="translationHasSupportBankDetailsHtml" />
          </WdsAlert>
        </div>
      </div>
    </template>

    <template v-if="countryIsSupported">
      <div class="form-group">
        <div class="col-lg-offset-4 col-lg-6">
          <div v-if="countryIsHidden" class="alert alert-info">
            {{
              $t(
                `We will review your bank account data and we may require you to setup
                a local bank account prior to completing any payments.`,
              )
            }}
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="col-md-4 control-label">{{ $t('Account holder’s name') }}</label>
        <div class="col-md-6">
          <div>
            <input
              v-model="bankAccountInternal.beneficiary_name"
              name="bank[beneficiaryName]"
              class="form-control"
              required
            />
            <span class="required-asterix">*</span>
          </div>
          <span class="small m-l-10 c-gray">
            {{
              $t(
                `Please enter the name exactly as it is registered with your bank. This will either be your own name or (if it's a business account) the name of your company.`,
              )
            }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <label class="col-md-4 control-label">{{ $t('Name of bank') }}</label>
        <div class="col-md-6">
          <input
            v-model="bankAccountInternal.bank_name"
            name="bank[bankName]"
            class="form-control"
            :required="hasDirtyFields"
          />
          <span class="required-asterix">*</span>
        </div>
      </div>

      <template v-if="bban">
        <div v-for="(field, key) in bban" :key="key" class="form-group">
          <label class="col-md-4 control-label">{{ $t(field.name) }}</label>
          <div class="col-md-4">
            <input
              v-model="bankAccountInternal.bban[key]"
              :name="`bank[bban][${camelCase(key)}]`"
              class="form-control"
              :required="hasDirtyFields"
              :readonly="field.readonly"
            />
            <span class="required-asterix">*</span>
          </div>
        </div>

        <div v-if="selectedCountry.bban_swift" class="form-group">
          <label class="col-md-4 control-label">{{ $t('SWIFT') }}</label>
          <div class="col-md-6">
            <input v-model="bankAccountInternal.swift" name="bank[swift]" class="form-control" />
            <span class="required-asterix">*</span>
          </div>
        </div>
      </template>

      <template v-if="showIban && bban">
        <div class="col-md-offset-4 col-md-8">
          <h4>{{ $t('International bank details') }}</h4>
          <p class="small c-gray">
            {{ $t('We recommend that you use a local bank account instead of IBAN.') }}
          </p>
        </div>
      </template>

      <div v-if="showIban">
        <div class="form-group">
          <label class="col-md-4 control-label">{{ $t('IBAN') }}</label>
          <div class="col-md-6">
            <input
              v-model="bankAccountInternal.iban"
              name="bank[iban]"
              class="form-control"
              :required="hasDirtyFields"
            />
            <span class="required-asterix">*</span>
          </div>
        </div>
        <div class="form-group">
          <label class="col-md-4 control-label">{{ $t('SWIFT/BIC') }}</label>
          <div class="col-md-6">
            <input
              v-model="bankAccountInternal.swift"
              name="bank[swift]"
              class="form-control"
              :required="hasDirtyFields"
            />
            <span class="required-asterix">*</span>
          </div>
        </div>
      </div>

      <div v-if="countryIsHidden" class="form-group">
        <label class="col-md-4 control-label">{{ $t('Bank address') }}</label>
        <div class="col-md-6">
          <textarea
            v-model="bankAccountInternal.bank_address"
            rows="4"
            name="bank[bankAddress]"
            class="form-control"
            :required="hasDirtyFields"
          />
          <span class="small m-l-10 c-gray">
            {{
              $t(
                'Enter the full address incl. postcode of the bank, where the bank account is registered.',
              )
            }}
          </span>
        </div>
      </div>
    </template>
  </div>

  <div class="form-group">
    <div class="col-md-6 col-md-offset-4">
      <WdsTooltip
        v-if="!countryIsSupported"
        :text="$t('Please contact our support to update the bank details')"
      >
        <SubmitButton :text="$t('Save')" disabled />
      </WdsTooltip>
      <SubmitButton v-else :text="$t('Save')" auto-submit />
    </div>
  </div>
</template>

<script>
import { camelCase } from 'lodash'
import { WdsInputArea, WdsTooltip, WdsAlert } from '@worksome/wds-components'
import SubmitButton from '@/components/SubmitButton.vue'
import BaseHtml from '@/components/BaseHtml.vue'

export default {
  components: { BaseHtml, WdsInputArea, SubmitButton, WdsTooltip, WdsAlert },
  props: {
    bankAccount: {
      required: true,
      type: Object,
    },
    countries: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      showAllCountries: false,
      country: this.bankAccount.bank_country,
      // Internl copy for v-models
      bankAccountInternal: { bban: {} },
    }
  },
  computed: {
    translationNeedSupportBankDetailsHtml() {
      return this.$t(
        `We do not support automatic transfers to :country. To add your bank details please <a href="#" class="intercomchat">chat to us</a>.`,
        { country: this.countryName },
      )
    },
    translationHasSupportBankDetailsHtml() {
      return this
        .$t(`The bank details shown are the ones you have most recently provided to Worksome.
            If you want to update them, please
            <a href="#" class="intercomchat">chat to us</a>
            .`)
    },
    countryOptions() {
      const options = []

      for (const [key, value] of Object.entries(this.countries)) {
        if (!value.hidden || this.showAllCountries) {
          options.push({
            label: value.name,
            value: key,
          })
        }
      }

      return options
    },
    selectedCountry() {
      return this.countries[this.country]
    },
    bban() {
      if (this.countries[this.country] === undefined) {
        return null
      }

      return this.countries[this.country].bban_fields
    },
    supportsIban() {
      if (this.countries[this.country] === undefined) {
        return false
      }

      return this.countries[this.country].supports_iban
    },
    showIban() {
      // If BBAN is not supported, show IBAN.
      if (!this.bban && this.supportsIban) {
        return true
      }
      // If IBAN is defined, show IBAN
      if (
        this.bankAccount.iban ||
        (this.bankAccount.swift && this.countries[this.country].bban_swift !== true)
      ) {
        return true
      }

      return false
    },
    countryIsHidden() {
      // We presume that if a country where to for some reason be a wrong type,
      // we would treat that case as if the country is hidden
      return this.countries[this.country]?.hidden ?? true
    },
    countryIsSupported() {
      return this.supportsIban || this.bban !== null
    },
    countryName() {
      return this.countries[this.country]?.name ?? ''
    },
    hasDirtyBbanFields() {
      return (
        this.bban &&
        Object.keys(this.bankAccountInternal.bban).some(
          (key) =>
            this.bankAccountInternal.bban?.[key] &&
            this.bankAccountInternal.bban?.[key] !== this.bankAccountSaturated.bban?.[key],
        )
      )
    },
    hasDirtyFields() {
      if (this.hasDirtyBbanFields) {
        return true
      }
      return Object.keys(this.bankAccountInternal).some(
        (key) =>
          typeof this.bankAccountInternal[key] !== 'object' &&
          this.bankAccountInternal[key] &&
          this.bankAccountInternal[key] !== this.bankAccountSaturated[key],
      )
    },
    hasManualPaymentDetails() {
      return !!this.bankAccount?.bban?.manual_payment_details
    },
    /**
     * Bban fields on bank account require their own individual
     * getters which may be null so we assess them here
     */
    bankAccountSaturated() {
      let bankAccount = { ...this.bankAccount, bban: {} }
      if (this.bban) {
        bankAccount = { ...bankAccount, bban: {} }
        Object.keys(this.bban).forEach((key) => {
          const bbanFieldValue = this.getBbanAttribute(key)
          if (bbanFieldValue) {
            bankAccount.bban[key] = bbanFieldValue
          }
          if (bankAccount.bban[key] === undefined && this.bban[key].default !== undefined) {
            bankAccount.bban[key] = this.bban[key].default
            this.bankAccountInternal.bban[key] = this.bban[key].default
          }
        })
      }
      return bankAccount
    },
  },
  mounted() {
    if (this.countryIsHidden) {
      this.showAllCountries = true
    }
    // Set internal copy since we need computeds
    this.bankAccountInternal = {
      ...this.bankAccountSaturated,
      bban: { ...this.bankAccountSaturated.bban },
    }
  },
  methods: {
    camelCase,
    getBbanAttribute(name) {
      if (
        this.bankAccount.bank_country !== this.country ||
        this.bankAccount.bban === undefined ||
        this.bankAccount.bban === null
      ) {
        return ''
      }

      return this.bankAccount.bban[name]
    },
    showInternationalOptions() {
      this.showAllCountries = true
      $('body').find('#bank-country-select').tooltip('show')
    },
    hideInternationalOptions() {
      this.showAllCountries = false

      // If the previously select country is no longer available, change it to the first available one.
      if (this.countryIsHidden) {
        this.country = this.countryOptions[0].value
      }
    },
  },
}
</script>
